import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { StepperRefAttributes } from 'primereact/stepper'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../../contexts'
import {
  EInvoiceDto,
  EinvoiceValidationresult,
} from '../../../../../../data/models'
import { EInvoiceValidator } from '../../../../../../data/models/EInvoice'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  DetailsDialog,
  ResultPanel,
  RuleSetsInfoDialog,
} from '../../../../common'
import {
  EInvoiceError,
  ExtendedEInvoiceValidationResult,
  requestDeleteEInvoice,
} from '../../../../data'
import { Footer } from '../footer'
import {
  getInvoiceFormatFromEInvoice,
  getInvoiceFormatFromError,
} from '../validation/utils'

interface ValidationResults {
  allErrors: ExtendedEInvoiceValidationResult[]
  allWarnings: ExtendedEInvoiceValidationResult[]
  allSuccess: ExtendedEInvoiceValidationResult[]
}

type Props = {
  eInvoice?: EInvoiceDto
  error?: EInvoiceError
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
}

export const InvoiceVerification: React.FC<Props> = ({
  eInvoice,
  error,
  stepperRef,
}) => {
  const { t } = useTranslation('eInvoiceDetails')
  const [isRuleSetsInfoDialogVisible, setIsRuleSetsInfoDialogVisible] =
    useState(false)
  const [isDetailsVisible, setDetailsVisible] = useState(false)
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [resultObject, setResultObject] =
    useState<EinvoiceValidationresult | null>(null)
  const { showToast } = useToastContext()

  const handleShowDetails = (result: EinvoiceValidationresult) => {
    setResultObject(result)
    setDetailsVisible(true)
  }
  // Choose where to get the errors from.
  // If you combine both sources, you get duplicates.
  // So if we have no errors, use the errors from eInvoice.
  const isFromErrorObject = !!error?.Results
  const errorsSource = error?.Results
    ? error?.Results
    : eInvoice?.ValidationResult?.Results || []
  const ruleSetsSource = eInvoice?.ValidationResult?.AvailableRuleSets
    ? eInvoice?.ValidationResult?.AvailableRuleSets
    : error?.AvailableRulesets || []
  const errorSource =
    error?.Results ?? eInvoice?.ValidationResult?.Results ?? []

  // Filter RuleSets for Rules Engine: validator === 1 (CustomValidation) - CompanyCodeRuleset
  const availableRuleSetsForCustomValidation = ruleSetsSource?.filter(rs => {
    if (rs.ruleset === 'CompanyCodeRuleset') return false
    return rs.validator === EInvoiceValidator.CustomValidation
  })

  const ruleSetNames = new Set(
    availableRuleSetsForCustomValidation?.map(rs => rs?.ruleset?.toLowerCase()),
  )

  const filteredResults = errorsSource?.filter(r => {
    return ruleSetNames.has(r.RuleSet?.toLowerCase() ?? '')
  })

  const invoiceFormat =
    getInvoiceFormatFromError(error?.InvoiceFormat) ||
    getInvoiceFormatFromEInvoice(eInvoice?.Meta?.file.subType)

  const invoiceVersion = error?.InvoiceVersion || eInvoice?.Meta?.file.version

  const originalEInvoice = eInvoice?.Attachments?.find(
    attachment => attachment.FileName === eInvoice.Meta?.file.fileName,
  )
  const fileName = originalEInvoice?.FileName || error?.FileName

  const validationResults = getValidationResults(errorSource, isFromErrorObject)

  const hasWarning = hasWarnings(errorSource, isFromErrorObject)
  const hasError = hasErrors(errorSource, isFromErrorObject)

  const deleteEinvoice = async () => {
    setIsRequestRunning(true)
    try {
      await requestDeleteEInvoice(eInvoice?.id!)

      showToast({
        severity: 'success',
        summary: t('title.delete.dialog.delete.messages.success.summary'),
        detail: t('title.delete.dialog.delete.messages.success.detail', {
          invoiceNumber: eInvoice?.Payload?.ID.Value?.Value ?? '',
        }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        severity: 'error',
        sticky: true,
        summary: t('title.delete.dialog.delete.messages.failure.summary'),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const getHeader = (): JSX.Element => {
    let header = t('stepper.verification.card.header.success.title')
    if (hasWarning) header = t('stepper.verification.card.header.warning.title')
    if (hasError) header = t('stepper.verification.card.header.error.title')

    return (
      <h2
        className={classNames('text-xl text-green-700', {
          'text-yellow-700': hasWarning,
          'text-red-700': hasError,
        })}
      >
        {header}
      </h2>
    )
  }

  const getDescription = (): JSX.Element => {
    let description = t(
      'stepper.verification.card.header.success.description',
      {
        invoiceFileName: fileName,
      },
    )

    if (hasWarning)
      description = t('stepper.verification.card.header.warning.description', {
        invoiceFileName: fileName,
      })
    if (hasError)
      description = t('stepper.verification.card.header.error.description', {
        invoiceFileName: fileName,
      })

    return (
      <p
        className={classNames('text-lg text-green-700', {
          'text-yellow-700': hasWarning,
          'text-red-700': hasError,
        })}
      >
        {description}
        <Button
          aria-label={t('stepper.validation.card.steps.conformance.info.label')}
          icon="pi pi-info-circle"
          rounded
          text
          size="small"
          onClick={handleRuleSetsInfoClick}
          className="border-circle p-1 h-2rem w-2rem"
        />
      </p>
    )
  }

  const handleRuleSetsInfoClick = (e: React.MouseEvent) => {
    setIsRuleSetsInfoDialogVisible(true)
  }

  return (
    <>
      <RuleSetsInfoDialog
        title={t('stepper.verification.card.header.info.dialog.title')}
        invoiceFormat={invoiceFormat}
        invoiceVersion={invoiceVersion}
        availableRuleSets={availableRuleSetsForCustomValidation}
        results={filteredResults}
        visible={isRuleSetsInfoDialogVisible}
        setVisible={setIsRuleSetsInfoDialogVisible}
        showSuccessColumn
      />

      <DetailsDialog
        resultObject={resultObject}
        visible={isDetailsVisible}
        setVisible={setDetailsVisible}
      />

      {/* Card */}
      <div className="border-round-lg shadow-1 overflow-hidden">
        {/* Header */}
        <div
          className={classNames('border-top-3	py-6', {
            'border-red-500 bg-red-50': hasError,
            'border-yellow-500 bg-yellow-50': hasWarning,
            'border-green-500 bg-green-50': !hasError && !hasWarning,
          })}
        >
          <div
            className="line-height-3 text-center mx-auto mb-6 pb-3"
            style={{ maxWidth: '600px' }}
          >
            {getHeader()}
            {getDescription()}
          </div>
        </div>

        {/* Tables */}
        {(hasError || hasWarning) && (
          <div
            className="md:pb-4"
            style={{
              backgroundImage: hasError
                ? 'linear-gradient(to bottom, var(--red-50) 0%, var(--surface-a) 120px)'
                : 'linear-gradient(to bottom, var(--yellow-50) 0%, var(--surface-a) 120px)',
            }}
          >
            {/* All Errors */}
            {hasError && (
              <ResultPanel
                results={
                  validationResults.allErrors as EinvoiceValidationresult[]
                }
                status="Error"
                title={t('stepper.verification.card.content.errors.title', {
                  count: validationResults.allErrors.length,
                })}
                onInfoClick={handleShowDetails}
              />
            )}

            {/* All Warnings */}
            {hasWarning && (
              <ResultPanel
                results={
                  validationResults.allWarnings as EinvoiceValidationresult[]
                }
                status="Warn"
                title={t('stepper.verification.card.content.warnings.title', {
                  count: validationResults.allWarnings.length,
                })}
                onInfoClick={handleShowDetails}
              />
            )}
          </div>
        )}
        <div
          className="md:pb-4"
          style={{
            backgroundImage:
              !hasError && !hasWarning
                ? 'linear-gradient(to bottom, var(--green-50) 0%, var(--surface-a) 120px)'
                : undefined,
          }}
        >
          {/* All Success Results */}
          <ResultPanel
            results={validationResults.allSuccess as EinvoiceValidationresult[]}
            status="Success"
            title={t('stepper.verification.card.content.success.title', {
              count: validationResults.allSuccess.length,
            })}
            onInfoClick={handleShowDetails}
          />
        </div>
      </div>

      <Footer
        stepperRef={stepperRef}
        hasError={hasError}
        isVerficationStep={true}
        deleteEinvoice={deleteEinvoice}
        isRequestRunning={isRequestRunning}
      />
    </>
  )
}

const getValidationResults = (
  errors?: ExtendedEInvoiceValidationResult[],
  isFromErrorObject: boolean = false,
): ValidationResults => {
  // Extract validation results by type
  // If data comes from error object, show all results
  // Otherwise, filter by RulesEngine only
  const filteredResults = isFromErrorObject
    ? errors || []
    : errors?.filter(error => error.ValidationType === 'RulesEngine') || []

  const errorResults = filteredResults.filter(error => error.Level === 'Error')
  const warningResults = filteredResults.filter(error => error.Level === 'Warn')
  const successResults = filteredResults.filter(
    error => error.Level === 'Success',
  )

  return {
    allErrors: errorResults,
    allWarnings: warningResults,
    allSuccess: successResults,
  }
}

const hasWarnings = (
  errors?: ExtendedEInvoiceValidationResult[],
  isFromErrorObject: boolean = false,
): boolean => {
  if (isFromErrorObject) {
    return errors?.some(error => error.Level === 'Warn') ?? false
  }
  return (
    errors?.some(
      error => error.Level === 'Warn' && error.ValidationType === 'RulesEngine',
    ) ?? false
  )
}

const hasErrors = (
  errors?: ExtendedEInvoiceValidationResult[],
  isFromErrorObject: boolean = false,
): boolean => {
  if (isFromErrorObject) {
    return errors?.some(error => error.Level === 'Error') ?? false
  }
  return (
    errors?.some(
      error =>
        error.Level === 'Error' && error.ValidationType === 'RulesEngine',
    ) ?? false
  )
}
